import { useEffect, useMemo, useRef, useState } from "react";
import {
  getActionItems,
  getProjectTaskList,
  TaskSummary,
} from "../../ProjectManagement/Service/TaskService";
import { Link, useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { getRawPermissionsArray } from "../../../../Routes/AuthGard/usePermissions";

const SkBox = ({ w, h, r = 6, style = {} }) => (
  <div
    style={{
      width: w,
      height: h,
      borderRadius: r,
      background:
        "linear-gradient(90deg,var(--sk-base,#e9e9e9) 25%,var(--sk-shine,#f4f4f4) 50%,var(--sk-base,#e9e9e9) 75%)",
      backgroundSize: "600px 100%",
      animation: "skShimmer 1.4s infinite linear",
      flexShrink: 0,
      ...style,
    }}
  />
);

const SkeletonStyles = () => (
  <style>{`
    @keyframes skShimmer {
      0%   { background-position: -600px 0; }
      100% { background-position:  600px 0; }
    }
  `}</style>
);

const ActionCardSkeleton = () => (
  <div className="stack-section">
    <SkeletonStyles />
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <SkBox w={8} h={8} r={50} />
        <SkBox w={110} h={13} />
      </div>
      <SkBox w={90} h={13} />
    </div>

    <div style={{ position: "relative", height: 127 }}>
      <div style={{
        position: "absolute", left: 0, right: 0, top: 26,
        height: 100, borderRadius: 10, overflow: "hidden",
        transform: "scale(0.94)", transformOrigin: "top center", zIndex: 1, opacity: 0.4,
      }}>
        <SkBox w="100%" h="100%" r={10} />
      </div>
      <div style={{
        position: "absolute", left: 0, right: 0, top: 13,
        height: 100, borderRadius: 10, overflow: "hidden",
        transform: "scale(0.97)", transformOrigin: "top center", zIndex: 2, opacity: 0.65,
      }}>
        <SkBox w="100%" h="100%" r={10} />
      </div>
      <div style={{
        position: "absolute", left: 0, right: 0, top: 0,
        height: 100, borderRadius: 10, padding: 14,
        background: "linear-gradient(90deg,var(--sk-base,#e9e9e9) 25%,var(--sk-shine,#f4f4f4) 50%,var(--sk-base,#e9e9e9) 75%)",
        backgroundSize: "600px 100%",
        animation: "skShimmer 1.4s infinite linear",
        zIndex: 3,
      }}>
        <SkBox w={90} h={11} style={{ marginBottom: 10, opacity: 0.5 }} />
        <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 10 }}>
          <SkBox w={32} h={32} r={8} style={{ opacity: 0.5 }} />
          <div>
            <SkBox w={40} h={22} style={{ marginBottom: 5, opacity: 0.5 }} />
            <SkBox w={80} h={10} style={{ opacity: 0.4 }} />
          </div>
        </div>
        <SkBox w={110} h={28} r={6} style={{ opacity: 0.45 }} />
      </div>
    </div>

    <div style={{ display: "flex", gap: 6, justifyContent: "center", marginTop: 10 }}>
      <SkBox w={22} h={7} r={4} />
      <SkBox w={7} h={7} r={50} />
      <SkBox w={7} h={7} r={50} />
    </div>
    {/* hint */}
    <div style={{ textAlign: "center", marginTop: 6 }}>
      <SkBox w={160} h={11} style={{ margin: "0 auto" }} />
    </div>
  </div>
);

const ProjectSummarySkeleton = () => (
  <div className="card" style={{ flex: "1 1 0%" }}>
    <SkeletonStyles />
    <div className="ch">
      <SkBox w={130} h={15} />
      <SkBox w={80} h={13} style={{ marginLeft: "auto" }} />
    </div>
    <div
      className="task-sstrip mt-3 mb-0"
      style={{ padding: "0 12px 12px", display: "grid", gridTemplateColumns: "repeat(6,1fr)", gap: 10, marginTop: 12 }}
    >
      {Array.from({ length: 6 }).map((_, i) => (
        <div
          key={i}
          style={{
            background: "var(--sk-base,#e9e9e9)",
            borderRadius: 8,
            padding: "10px 12px",
          }}
        >
          <SkBox w="70%" h={11} style={{ marginBottom: 8 }} />
          <SkBox w="40%" h={22} />
        </div>
      ))}
    </div>
  </div>
);

const MyTasksSkeleton = () => (
  <div className="card" style={{ flex: "1 1 0%" }}>
    <SkeletonStyles />
    <div className="ch">
      <SkBox w={80} h={15} />
      <SkBox w={24} h={18} r={10} style={{ marginLeft: "auto" }} />
      <SkBox w={40} h={13} />
    </div>
    <div style={{ padding: "9px 11px" }}>
      {Array.from({ length: 5 }).map((_, i) => (
        <div
          key={i}
          className="mytask-row"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            padding: "10px 0",
            borderBottom: i < 4 ? "0.5px solid var(--sk-base,#e9e9e9)" : "none",
            cursor: "default",
          }}
        >
          <div style={{ flex: "1 1 0%", minWidth: 0 }}>
            <SkBox w={`${65 + (i % 3) * 10}%`} h={13} style={{ marginBottom: 7 }} />
            <SkBox w={`${40 + (i % 4) * 8}%`} h={11} />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
            <SkBox w={56} h={20} r={10} />
            <SkBox w={26} h={26} r={50} />
          </div>
        </div>
      ))}
    </div>
  </div>
);

export const DashboardTask = ({ activityData = {}, loading: propLoading = false } ) => {
  const [IsLoading, setIsLoading] = useState(false);
  const [IsTaskLoading, setIsTaskLoading] = useState(false);
  const [IsSummaryLoading, setIsSummaryLoading] = useState(false);
  const [projectSummary, setProjectSummary] = useState(activityData?.Summary || []);
  const userDetails = useMemo(() => {
    return JSON.parse(localStorage.getItem("User") || "{}");
  }, []);

  useEffect(() => {
    setProjectSummary(activityData?.Summary || []);
  }, [activityData]);
  const [taskList, setTaskList] = useState([]);
  const history = useHistory();
  const [totalTaskCount, setTotalTaskCount] = useState(0);
  const [activeCard, setActiveCard] = useState(0);
  const [stackWrapHeight, setStackWrapHeight] = useState("auto");
  const stackWrapRef = useRef(null);
  const cardRefs = useRef([]);
  const [actionItems, setActionItems] = useState();
  const [stackCards, setStackCards] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState([]);
  let userPermissions = getRawPermissionsArray();
  const [cardAction, setCardAction] = useState(false);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("User") || "{}");
    const statuses = user?.OrgTaskStatus;
    if (statuses?.length) {
      const filteredIds = statuses
        .filter((item) => item.CategoryId === 1)
        .map((item) => item.Id);
      console.log("Filtered Status IDs:", filteredIds);
      setSelectedStatus(filteredIds);
      getProjectTask(filteredIds);
    }
    permissionList();
    const updateStackHeight = () => {
      const topCard = cardRefs.current[activeCard];
      if (topCard) {
        setStackWrapHeight(`${topCard.offsetHeight + 36}px`);
      }
    };
    updateStackHeight();
  }, [activeCard]);

  const permissionList = () => {
    if (userPermissions) {
      userPermissions?.map((UserPermission) => {
        if (UserPermission?.PermissionName == "Action.Read") {
          setCardAction(true);
        }
      });
    }
  };

  const getCardStyle = (index) => {
    const offset = (index - activeCard + stackCards.length) % stackCards.length;
    if (offset === 0) return { top: 0, zIndex: 3, transform: "scale(1)" };
    if (offset === 1) return { top: 13, zIndex: 2, transform: "scale(0.97)" };
    return { top: 26, zIndex: 1, transform: "scale(0.94)" };
  };

  const handleCardClick = () => {
    if (!stackCards.length) return;
    setActiveCard((prev) => (prev + 1) % stackCards.length);
  };

  const handleDotClick = (index) => {
    setActiveCard(index);
  };

  useEffect(() => {
    GetActionItem();
  }, []);



  const mapToStackCards = (data) => {
    return data.map((item, index) => ({
      id: `c${index + 1}`,
      background: item.ColorCode,
      title: item.ActionTypeDescription,
      count: item.ActionPendingCount,
      subtitle: item.ActionText,
      buttonText: item.ActionButtonText,
      actionType: item.ActionType,
      icon: (
        <img
          src={item.ActionIcon}
          alt={item.ActionType}
          style={{ width: 20, height: 20 }}
        />
      ),
      buttonIcon: (
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="#fff" strokeWidth="1.5">
          <path d="M2 6h8M7 2.5l3.5 3.5L7 9.5" />
        </svg>
      ),
    }));
  };

  const actionRouteMap = {
    Leave: "/TeamLeave",
    WFH: "/TeamWfh",
    Task: "/Project/Task/List",
  };

  const handleNavigate = (card) => {
    const route = actionRouteMap[card.actionType];
    if (route) {
      history.push(route);
    } else {
      console.warn("No route defined for:", card.actionType);
    }
  };

  async function GetActionItem() {
    setIsLoading(true);
    try {
      const response = await getActionItems();
      if (response?.StatusCode === 2000) {
        const mappedCards = mapToStackCards(response.Data);
        setStackCards(mappedCards);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }

  async function getProjectTask(filteredIds) {
    const params = {
      OrgId: userDetails?.OrgId,
      LoggedInEmpId: userDetails?.EmpId,
      RaisedByEmpId: "",
      AssignedToId: userDetails?.EmpId?.toString(),
      TaskId: 0,
      StatusId: filteredIds?.join(",") || "",
      ModuleId: "",
      SubModuleId: "",
      PriorityId: "",
      SeverityId: "",
      Search: "",
      FilterStartDate: "",
      FilterEndDate: "",
      PageNumber: 1,
      PageSize: 5,
      IsDeleted: 0,
      SprintId: 0,
      TaskNumber: "",
      TaskTypeId: "",
      SegmentId: "",
      SortBy: "",
      SortOrder: "",
    };
    setIsTaskLoading(true);
    try {
      const data = await getProjectTaskList(params);
      const list = data?.Data?.OrgEmpTasks || [];
      setTaskList(list);
      setTotalTaskCount(data?.Data?.TotalTaskCount);
      setIsTaskLoading(false);
    } catch (error) {
      console.log(error);
    } finally {
      setIsTaskLoading(false);
    }
  }

  return (
    <div>
      {/* ── Section 1: Action Required ── */}
      <div className="col-xl-12 task-mgmt-section mb-3">
        {IsLoading ? (
          <ActionCardSkeleton />
        ) : (
          <>
            {cardAction && stackCards?.length > 0 && (
              <div className="stack-section">
                <div className="stack-header">
                  <div className="stack-header-left">
                    <span className="stack-dot"></span>
                    Action required
                  </div>
                  {stackCards?.length > 0 && (
                    <span>{stackCards?.length} pending items</span>
                  )}
                </div>

                <div
                  className="stack-wrap"
                  id="stackWrap"
                  ref={stackWrapRef}
                  style={{ minHeight: "127px", height: "127px", position: "relative" }}
                >
                  {stackCards.map((card, index) => (
                    <div
                      className="scard"
                      key={card.id}
                      id={card.id}
                      ref={(el) => (cardRefs.current[index] = el)}
                      style={{
                        background: card.background,
                        position: "absolute",
                        left: 0,
                        width: "100%",
                        ...getCardStyle(index),
                      }}
                      onClick={() => handleCardClick(index)}
                    >
                      <div className="sc-title-label">{card.title}</div>
                      <div className="sc-top-row">
                        <div className="sc-icon">{card.icon}</div>
                        <div>
                          <div className="sc-count">
                            {card.count}
                            <div className="sc-sub">{card.subtitle}</div>
                          </div>
                        </div>
                      </div>
                      <button
                        className="sc-btn"
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleNavigate(card);
                        }}
                      >
                        {card.buttonText}
                        {card.buttonIcon}
                      </button>
                    </div>
                  ))}
                </div>

                <div className="dot-nav">
                  {stackCards.map((card, index) => (
                    <button
                      key={card.id}
                      type="button"
                      onClick={() => handleDotClick(index)}
                      style={{
                        width: activeCard === index ? 22 : 7,
                        background: activeCard === index ? "#EC4119" : "#d1d5db",
                        border: "none",
                        padding: 0,
                      }}
                    />
                  ))}
                </div>

                <p className="sc-hint">Click card to cycle · hover to reveal action</p>
              </div>
            )}
          </>
        )}
      </div>

      {/* ── Section 2: Project Summary ── */}
      <div className="col-xl-12 task-mgmt-section mb-3">
        {IsSummaryLoading ? (
          <ProjectSummarySkeleton />
        ) : (
          <div className="card" style={{ flex: "1 1 0%" }}>
            <div className="ch">
              <strong className="upcome">Project Summary</strong>
              <Link
                to={`/Project/Dashboard`}
                className="btn btn-ghost btn-sm p-0 bg-transparent mla"
              >
                Dashboard <i className="fa-solid fa-arrow-right"></i>
              </Link>
            </div>
            <div
              className="task-sstrip mt-3 mb-0"
              id="kpiStrip"
              style={{ padding: "0 12px 12px" }}
            >
              <div className="sc purple-active-task">
                <div className="sc-lab">Total Projects</div>
                <div className="sc-val">{projectSummary?.TotalProjects || 0}</div>
              </div>
              <div className="sc active-task">
                <div className="sc-lab">Active Projects</div>
                <div className="sc-val">{projectSummary?.ActiveProjects || 0}</div>
              </div>
              <div className="sc blue-active-task">
                <div className="sc-lab">Inactive Projects</div>
                <div className="sc-val">{projectSummary?.InActiveProjects || 0}</div>
              </div>
              <div className="sc purple-active-task">
                <div className="sc-lab">Total Tasks</div>
                <div className="sc-val">{projectSummary?.TotalTasks || 0}</div>
              </div>
              <div className="sc active-task">
                <div className="sc-lab">Active Tasks</div>
                <div className="sc-val">{projectSummary?.ActiveTasks || 0}</div>
              </div>
              <div className="sc red-active-task">
                <div className="sc-lab">Overdue Tasks</div>
                <div className="sc-val">{projectSummary?.OverdueTasks || 0}</div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Section 3: My Tasks ── */}
      <div className="col-xl-12 mytask-mgmt-section mb-3">
        {IsTaskLoading ? (
          <MyTasksSkeleton />
        ) : (
          <div className="card" style={{ flex: "1 1 0%" }}>
            <div className="ch">
              <strong className="upcome">My Tasks</strong>
              <span className="badge bg-inprog mla" id="myTaskCount">
                {totalTaskCount}
              </span>
              <Link
                to={`/Project/Task/List`}
                className="btn btn-ghost btn-sm p-0 bg-transparent "
              >
                All <i className="fa-solid fa-arrow-right"></i>
              </Link>
            </div>
            <div
              id="myTasksList"
              style={{
                overflowY: "auto",
                maxHeight: "calc(-280px + 100vh)",
                padding: "9px 11px",
                textAlign: "center",
              }}
            >
              {taskList?.length === 0 && <span>No task found</span>}
              {taskList
                ?.filter((t) => t?.StatusName !== "Done")
                ?.map((t) => (
                  <div
                    className="mytask-row"
                    key={t?.Id}
                    onClick={() =>
                      history.push(`/Project/Task/Detail/${t?.GUID}`)
                    }
                  >
                    <div style={{ flex: "1 1 0%", minWidth: "0px" }}>
                      <div className="mytask-title">
                        {t?.TaskTitle?.slice(0, 80) || "N/A"}{" "}
                        {t?.TaskTitle?.length > 80 && <span>...</span>}
                      </div>
                      <div className="mytask-proj">
                        <span>{t?.TaskNumber}</span> |{" "}
                        <span>{t?.ProjectName}</span> -{" "}
                        <span>{t?.ModuleName || "N/A"} </span>-{" "}
                        <span>{t?.SubModuleName || "N/A"}</span>
                      </div>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "flex-end",
                        gap: "3px",
                        flexShrink: 0,
                      }}
                    >
                      <span
                        className="badge bg-low"
                        style={{ fontSize: "10px" }}
                      >
                        {t?.StatusName}
                      </span>
                      <div className="image">
                        <img
                          src={
                            t?.AssignToProfileImage ||
                            "/images/placeholder_profile.png"
                          }
                          width="100%"
                          style={{ opacity: "1", borderRadius: "100%" }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardTask;